import { TFunction } from 'react-i18next'
import { Box, Checkbox, IconButton, Tooltip } from '@mui/material'
import { icons } from '../../../../utils/icons'
import { ItemMenu } from '../../../../components/baseMenu'
import { Dispatch, SetStateAction, useState } from 'react'
import { Item } from '../../../../utils/types/generals'
import { Constants } from '../../../../utils/constants'
import { Roles } from '../../../../utils/enums'
import { useStyles } from './styles'
import { useNavigate } from 'react-router-dom'
import { menuItems } from './constants'
import { HeaderRowCheckBox } from '../../../calendar/pages/main/components/headerRowCheckBox'
import { useContextState } from '@/hooks/theme'
import { InputModel } from '@/utils/types'

export interface ItemsInput {
  questionnaire?: any[]
  divisions?: any[]
  user?: any[]
  stores?: any[]
  zone?: any[]
  userType?: any[]
  question?: any[]
  questionDisabled?: boolean
}

export const useInput = (items?: ItemsInput) => {
  return {
    search: {
      label: 'general.search',
      translate: true,
      name: 'search',
      type: 'text',
      value: null,
    },
    from: {
      label: 'general.tags.from',
      translate: true,
      name: 'from',
      type: 'date',
      value: null,
      rest: {
        max: 'to',
      },
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
      },
    },
    to: {
      label: 'general.tags.to',
      translate: true,
      name: 'to',
      type: 'date',
      value: null,
      rest: {
        min: 'from',
      },
      size: {
        xl: 6,
        lg: 6,
        md: 6,
        sm: 6,
      },
    },
    questionnaire: {
      label: 'general.tags.questionnaire',
      translate: true,
      name: 'questionnaire',
      type: 'select-search',
      items: items?.questionnaire ?? [],
      value: null,
    },
    question: {
      label: 'general.tags.question',
      translate: true,
      name: 'question',
      type: 'filter-select',
      items: items?.question ?? [],
      value: null,
      rest: {
        disabled: items?.questionDisabled ?? true,
      },
    },
    userType: {
      label: 'general.tags.userType',
      translate: true,
      name: 'userType',
      type: 'filter-select',
      items: items?.userType ?? [],
      value: null,
      notAllowedRoles: [Roles.SUPERVISOR, Roles.AGENT, Roles.STORE, Roles.GERENT, Roles.SUPPORT, Roles.AUDIT, Roles.PARTNER],
    },
    user: {
      label: 'general.tags.user',
      translate: true,
      name: 'user',
      type: 'filter-select',
      items: items?.user ?? [],
      value: null,
      notAllowedRoles: [Roles.SUPERVISOR, Roles.AGENT, Roles.STORE, Roles.GERENT, Roles.SUPPORT, Roles.AUDIT, Roles.PARTNER],
    },
    stores: {
      label: 'general.tags.store',
      translate: true,
      name: 'stores',
      type: 'filter-select',
      items: items?.stores ?? [],
      value: null,
      notAllowedRoles: [Roles.SUPERVISOR, Roles.AGENT, Roles.STORE, Roles.GERENT, Roles.SUPPORT, Roles.AUDIT, Roles.PARTNER],
    },
    divisions: {
      label: 'general.tags.division',
      translate: true,
      name: 'divisions',
      type: 'filter-select',
      items: items?.divisions ?? [],
      value: null,
    },
    zone: {
      label: 'general.tags.zone',
      translate: true,
      name: 'zone',
      type: 'filter-select',
      items: items?.zone ?? [],
      value: null,
    },
  } as { [x: string]: InputModel }
}

export const useColumn = (
  t: TFunction<'translation', undefined>,
  rowLength: number,
  setIsChecked: Dispatch<SetStateAction<boolean>>,
  selectedIds: number[],
  isChecked: boolean,
  setSelectedIds: Dispatch<SetStateAction<number[]>>,
  notSelected: number[],
  setNotSelected: Dispatch<SetStateAction<number[]>>,
  handleDeleteUniqueOpen: () => void,
) => {
  const styles = useStyles()
  const { colors } = useContextState() 
  const navigate = useNavigate()
  const [detail, setDetail] = useState<{
    id: number
    positionMenu: Record<string, unknown>
    anchor: null | HTMLButtonElement
    menu: ItemMenu[]
  }>({
    id: 0,
    positionMenu: {},
    anchor: null,
    menu: menuItems,
  })

  const closeMenu = () => {
    setDetail((prev) => ({
      ...prev,
      anchor: null,
    }))
  }

  const handleHeaderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedIds([])
    setNotSelected([])
    if (event.target.checked) {
      setIsChecked(true)
    } else {
      setIsChecked(false)
    }
  }

  const optionMenu = ({ item }: { item: Item }) => {
    switch (item.value) {
      case 1:
        navigate(`/dashboard/action-plan/to-generate/${detail.id}`)
        break
      case 2:
        navigate(`/dashboard/action-plan/to-generate/${detail.id}/create`)
        break
      case 3:
        handleDeleteUniqueOpen()
        break
    }
  }

  return {
    detail,
    closeMenu,
    optionMenu,
    column: [
      {
        field: 'checkbox',
        headerName: '',
        headerCell: {
          ...styles?.mineHeaderTable,
          textAlign: 'center',
          verticalAlign: 'middle',
        },
        label: (
          <Checkbox
            checked={isChecked}
            onChange={handleHeaderChange}
            color="primary"
          />
        ),
        renderCell: (params: any) => {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <HeaderRowCheckBox
                id={params.row.id}
                isChecked={isChecked}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                notFolioSelected={notSelected}
                setNotFolioSelected={setNotSelected}
                disabled={false}
              />
            </Box>
          )
        },
      },
      {
        id: 'divisions',
        label: t('general.tags.division'),
        sortable: true,
        minWidth: 110,
      },
      {
        id: 'stores',
        label: 'ID - Unidad',
        sortable: true,
        minWidth: 130,
        rowTdStyles: { color: colors.color10 },
        renderCell: (params: any) => {
          return params?.row?.stores
        },
      },
      {
        id: 'attentionType',
        label: 'Tipo de agendamiento',
        sortable: true,
        minWidth: 130,
      },
      {
        id: 'questionnaire',
        label: t('general.tags.questionnaire'),
        sortable: true,
        minWidth: 130,
      },
      {
        id: 'answersCount',
        label: (
          <Box sx={{ lineHeight: 1.4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box>Cant. ítems</Box>
            <Box>de respuesta</Box>
          </Box>
        ),
        sortable: true,
        minWidth: 100,
        headerCell: {
          '& .MuiTableSortLabel-root': {
            justifyContent: 'center',
            width: '100%',
          },
        },
        rowTdStyles: { textAlign: 'center', padding: '0 30px 0 0', color: colors.color10 },
        renderCell: (params: any) => {
          return `${params.row.answersCount} items`
        },
      },
      {
        id: 'userCreator',
        label: 'Usuario creador',
        sortable: true,
        minWidth: 130,
      },
      {
        id: 'questionnaireDates',
        label: (
          <Box sx={{ lineHeight: 1.4 }}>
            <Box>Inicio respuesta de cuestionario</Box>
            <Box>Fin de respuesta de cuestionario</Box>
          </Box>
        ),
        sortable: true,
        minWidth: 180,
        rowTdStyles: { color: colors.color10 },
        renderCell: (params: any) => {
          return (
            <Box sx={{ lineHeight: 1.4 }}>
              <Box>{params.row.startDate?.label}</Box>
              <Box>{params.row.endDate?.label}</Box>
            </Box>
          )
        },
      },
      {
        id: 'action',
        label: t('general.tags.action'),
        minWidth: 80,
        renderCell: (params: any) => {
          const id = params?.row?.id
          return (
            <Box sx={styles.containerStatus}>
              <Tooltip title={t('ToolTips.seeActions')}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setDetail({
                      id,
                      positionMenu:
                        rowLength < Constants.numberOfRows
                          ? {
                              top: 0,
                              left: '-30px',
                            }
                          : {
                              top: `${e.clientY}px`,
                              left: `${e.clientX - 80}px`,
                            },
                      anchor: e.currentTarget,
                      menu: menuItems,
                    })
                  }}
                >
                  {icons('moreHoriz')}
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
      },
    ],
  }
}
